import { TTableTreeModel, TTextCell } from '../../table.type';

export type nextExtFieldMovementDirection = 'VERTICAL' | 'HORIZONTAL';
export const ExternalInputPadUtil = {
  selectExtEditableInputBasedOnRowCellIndex: <T>(
    tableModel: TTableTreeModel<T>,
    rowAndCellIndex: { rowIndex: number; cellIndex: number },
  ) => {
    const updateModel = { ...tableModel };

    updateModel.tableInfo.rows = updateModel.tableInfo.rows.map((row, index) => {
      if (index === rowAndCellIndex.rowIndex) {
        row.cells = row.cells.map((rowCell, index) => {
          if (rowCell.type == 'TEXT_CELL' && rowCell.isExternalInput) {
            if (index === rowAndCellIndex.cellIndex) {
              rowCell.isExternalInput.editStatus = true;
            } else {
              rowCell.isExternalInput.editStatus = false;
            }
          }
          return rowCell;
        });
      } else {
        row.cells = row.cells.map((rowCell) => {
          if (rowCell.type == 'TEXT_CELL' && rowCell.isExternalInput) {
            rowCell.isExternalInput.editStatus = false;
          }
          return rowCell;
        });
      }

      return row;
    });

    const numberPadValue = parseFloat(
      ExternalInputPadUtil.getExternalInputRowAndCellValueFromTableModel(updateModel, rowAndCellIndex),
    );
    return {
      model: updateModel,
      currentActiveRowCellIndex: rowAndCellIndex,
      numberPadValue: numberPadValue,
    };
  },
  getExternalInputRowAndCellValueFromTableModel<T>(
    tableModel: TTableTreeModel<T>,
    rowAndCellIndex: { rowIndex: number; cellIndex: number },
  ) {
    const selectedCellItem = tableModel.tableInfo.rows[rowAndCellIndex.rowIndex].cells[
      rowAndCellIndex.cellIndex
    ] as TTextCell;
    return selectedCellItem.text as string;
  },

  selectExtEditableInputBasedOnAction: <T>(
    tableModel: TTableTreeModel<T>,
    actionType: string,
    direction: nextExtFieldMovementDirection = 'HORIZONTAL',
  ) => {
    const updateModel = { ...tableModel };

    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(updateModel);
    let nextEditableCellAvailable = ExternalInputPadUtil.getNextPrevEditableCellAvailable(
      updateModel,
      currentActiveRowCellIndex,
      actionType,
      direction,
    );
    if (nextEditableCellAvailable['rowIndex'] !== -1) {
      (
        updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[
          currentActiveRowCellIndex['cellIndex']
        ] as TTextCell
      ).isExternalInput = {
        editStatus: false,
      };

      (
        updateModel.tableInfo.rows[nextEditableCellAvailable['rowIndex']].cells[
          nextEditableCellAvailable['cellIndex']
        ] as TTextCell
      ).isExternalInput = {
        editStatus: true,
      };
    } else {
      nextEditableCellAvailable = currentActiveRowCellIndex;
    }

    const numberPadValue = parseFloat(
      ExternalInputPadUtil.getExternalInputRowAndCellValueFromTableModel(updateModel, nextEditableCellAvailable),
    );
    return {
      model: updateModel,
      currentActiveRowCellIndex: nextEditableCellAvailable,
      numberPadValue: numberPadValue,
    };
  },
  updateSelectedExtEditableInputValue: <T>(tableModel: TTableTreeModel<T>, value: number | string) => {
    const updateModel = { ...tableModel };

    const currentActiveRowCellIndex = ExternalInputPadUtil.getCurrentActiveRowCellIndex(updateModel);
    if (currentActiveRowCellIndex['rowIndex'] !== -1) {
      (
        updateModel.tableInfo.rows[currentActiveRowCellIndex['rowIndex']].cells[
          currentActiveRowCellIndex['cellIndex']
        ] as TTextCell
      ).text = value;
    }

    const numberPadValue = parseFloat(
      ExternalInputPadUtil.getExternalInputRowAndCellValueFromTableModel(updateModel, currentActiveRowCellIndex),
    );
    return {
      model: updateModel,
      currentActiveRowCellIndex: currentActiveRowCellIndex,
      numberPadValue: numberPadValue,
    };
  },

  getCurrentActiveRowCellIndex<T>(tableModel: TTableTreeModel<T>) {
    const rowCellIndex = {
      rowIndex: -1,
      cellIndex: -1,
    };
    tableModel.tableInfo.rows.forEach((row, rowInd) => {
      row.cells.forEach((cell, cellInd) => {
        if (cell.type === 'TEXT_CELL') {
          if (cell.isExternalInput?.editStatus) {
            rowCellIndex['rowIndex'] = rowInd;
            rowCellIndex['cellIndex'] = cellInd;
          }
        }
      });
    });

    return rowCellIndex;
  },
  getNextPrevEditableCellAvailable<T>(
    tableModel: TTableTreeModel<T>,
    currentActiveRowCellIndex: {
      rowIndex: number;
      cellIndex: number;
    },
    actionType: string,
    direction: nextExtFieldMovementDirection,
  ) {
    const rowCellIndex = {
      rowIndex: -1,
      cellIndex: -1,
    };

    if (actionType === 'next') {
      // Start from the current cell
      if (direction === 'HORIZONTAL') {
        let startRowIndex = currentActiveRowCellIndex.rowIndex;
        let startCellIndex = currentActiveRowCellIndex.cellIndex + 1;

        for (let i = startRowIndex; i < tableModel.tableInfo.rows.length; i++) {
          const row = tableModel.tableInfo.rows[i];
          for (let j = i === startRowIndex ? startCellIndex : 0; j < row.cells.length; j++) {
            const cell = row.cells[j];
            if (cell.type === 'TEXT_CELL' && cell.isExternalInput && !cell.isExternalInput.editStatus) {
              rowCellIndex.rowIndex = i;
              rowCellIndex.cellIndex = j;
              return rowCellIndex;
            }
          }
        }
      } else if (direction === 'VERTICAL') {
        if (currentActiveRowCellIndex.rowIndex + 1 < tableModel.tableInfo.rows.length) {
          rowCellIndex.cellIndex = currentActiveRowCellIndex.cellIndex;
          rowCellIndex.rowIndex = currentActiveRowCellIndex.rowIndex + 1;
          return rowCellIndex;
        } else if (currentActiveRowCellIndex.rowIndex + 1 === tableModel.tableInfo.rows.length) {
          // Find next available Ext cell index;
          const row = tableModel.tableInfo.rows[currentActiveRowCellIndex.rowIndex];
          for (let j = currentActiveRowCellIndex.cellIndex + 1; j < row.cells.length; j++) {
            const cell = row.cells[j];
            if (cell.type === 'TEXT_CELL' && cell.isExternalInput && !cell.isExternalInput.editStatus) {
              rowCellIndex.rowIndex = 0;
              rowCellIndex.cellIndex = j;
              return rowCellIndex;
            }
          }
        }
      }
    } else if (actionType === 'prev') {
      if (direction === 'HORIZONTAL') {
        // Start from the current cell
        let startRowIndex = currentActiveRowCellIndex.rowIndex;
        let startCellIndex = currentActiveRowCellIndex.cellIndex - 1;

        for (let i = startRowIndex; i >= 0; i--) {
          const row = tableModel.tableInfo.rows[i];
          for (let j = i === startRowIndex ? startCellIndex : row.cells.length - 1; j >= 0; j--) {
            const cell = row.cells[j];
            if (cell.type === 'TEXT_CELL' && cell.isExternalInput && !cell.isExternalInput.editStatus) {
              rowCellIndex.rowIndex = i;
              rowCellIndex.cellIndex = j;
              return rowCellIndex;
            }
          }
        }
      } else if (direction === 'VERTICAL') {
        if (currentActiveRowCellIndex.rowIndex - 1 >= 0) {
          rowCellIndex.cellIndex = currentActiveRowCellIndex.cellIndex;
          rowCellIndex.rowIndex = currentActiveRowCellIndex.rowIndex - 1;
          return rowCellIndex;
        } else if (currentActiveRowCellIndex.rowIndex - 1 === -1) {
          // Find next available Ext cell index;
          const row = tableModel.tableInfo.rows[currentActiveRowCellIndex.rowIndex];
          for (let j = currentActiveRowCellIndex.cellIndex - 1; j >= 0; j--) {
            const cell = row.cells[j];
            if (cell.type === 'TEXT_CELL' && cell.isExternalInput && !cell.isExternalInput.editStatus) {
              rowCellIndex.rowIndex = tableModel.tableInfo.rows.length - 1;
              rowCellIndex.cellIndex = j;
              return rowCellIndex;
            }
          }
        }
      }
    }

    return rowCellIndex;
  },
};
